import { localeTag } from '@/i18n/strings';

/**
 * Byte and duration formatting.
 *
 * Both are locale-aware through `Intl` rather than string concatenation,
 * because a reader who has asked for a language with different digits or a
 * different decimal separator should see them here too — and because Section 14
 * requires 200 % text scaling to work, which means the width of these strings
 * cannot be assumed anywhere.
 */

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;

export function formatBytes(bytes: number): string {
  let value = Math.max(0, bytes);
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const unit = BYTE_UNITS[unitIndex] ?? 'byte';
  return new Intl.NumberFormat(localeTag, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  }).format(value);
}

/**
 * `m:ss`, or `h:mm:ss` past an hour.
 *
 * Deliberately not `Intl.DurationFormat`: it is not available in every browser
 * this client targets, and the frozen dependency set contains no polyfill.
 */
export function formatDuration(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return hours > 0
    ? `${String(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${String(minutes)}:${pad(seconds)}`;
}

/** Rounds a view count the way Section 06 requires it to be presented. */
export function formatApproximateCount(count: number): string {
  return new Intl.NumberFormat(localeTag, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(count);
}
